import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs } from "../../Styles";
import { AddLcModal, NewButtonList } from "..";
import { ButtonTypes, ListData_ValueTypes, SelectFieldOption_Values, TableStyles_Types } from "../../Definitions";
import { ReactiveTable } from "../Tables/reactiveTableRenderer";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, getPaginationRowModel, noop, useReactTable } from "@tanstack/react-table";
import { LanControllerInfo } from "../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLanControllersCall";
import { DynamicDataObk } from "../../Services";
import { OptionsWithRequiredNumberId, useLcIoOptions } from "../../Containers/FullViewScreens/GlobalScreens/Settings/_Settings/useLcIoOptions";
import { keyBy, merge } from "lodash";
import { GenericRow, useColumnConstructor } from "../Tables/_Tables/useColumnConstructor";
import { valueIsSelectFieldOptionsValue } from "../../Containers/FullViewScreens/GlobalScreens/Settings/_Settings/typeGuards";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../Store";
import { SET_EDIT_MODE } from "../../Store/UI/editModeSlice";
import { NewButton } from "../BaseComponents";

interface LcTableProps {
  lanControllerInfo: LanControllerInfo[];
  maintainControllerInfo?: (
    changed?: Partial<DynamicDataObk<any>> | Partial<DynamicDataObk<any[]>>,
    deleted?: Partial<DynamicDataObk<any>> | Partial<DynamicDataObk<any[]>>,
    created?: Partial<DynamicDataObk<any>> | Partial<DynamicDataObk<any[]>>
  ) => void;
}

interface BoardData extends GenericRow {
  id: number;
  cpuId?: SelectFieldOption_Values;
  name?: string;
  system?: OptionsWithRequiredNumberId;
  index: number;
}

export default function LcTable({ lanControllerInfo, maintainControllerInfo }: LcTableProps) {
  const { t } = useTranslation();
  const [addBoardModal, setAddBoardModal] = useState<boolean>(false);
  const { systemOptions, lanControllerCpuOptions, deviceCpuOptions } = useLcIoOptions();
  const dirtRef = useRef<LanControllerInfo[]>([]);

  const dispatchEditMode = useDispatch();
  const editMode = useSelector((state: RootState) => state.uiState.editMode.editMode && state.uiState.editMode.editType === 'lcTable');

  const [boardData, setBoardData] = useState<BoardData[]>([...lanControllerInfo].map((board, index) => {
    return {
      id: board.controller_id,
      cpuId: { id: board.cpu_id, label: board.cpu_id ? String(board.cpu_id) : "" },
      name: board.name,
      system: systemOptions.find((system) => system.id === board.system_id),
      index,
    };
  }));

  useEffect(() => setBoardData([...lanControllerInfo].map((board, index) => {
    return {
      id: board.controller_id,
      cpu_id: { id: board.cpu_id, label: board.cpu_id ?? "" },
      name: board.name,
      system_id: systemOptions.find((system) => system.id === board.system_id),
      index,
    };
  })), [lanControllerInfo, systemOptions]);

  const accessorData = useMemo(() => [
    { headerValue: t("devices.name", { defaultValue: "name" }), accessor: "name", type: ListData_ValueTypes.Button, width: "150px", staticColumn: true },
    { headerValue: t("devices.cpuId", { defaultValue: "cpu id" }), accessor: "cpu_id", type: ListData_ValueTypes.CreatableList, options: lanControllerCpuOptions, maxValueLength: 8, regex: true },
    { headerValue: t("devices.system", { defaultValue: "system" }), accessor: "system_id", type: ListData_ValueTypes.CreatableList, options: systemOptions },
  ], [lanControllerCpuOptions, systemOptions, t]);

  const columns = useColumnConstructor({
    accessorData,
    tableStyle: TableStyles_Types.primaryTable,
    canEdit: editMode,
  });

  const table = useReactTable({
    columns,
    data: boardData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      columnPinning: {
        left: ["name"],
      },
    },
    initialState: {
      pagination: {
        pageSize: 25,
      }
    },
    meta: {
      deleteRow: noop,
      updateData: (rowIndex, columnId, value, row) => {
        const index = row.original.index;
        if (index) {
          boardData[Number(index)][columnId] = value;
          setBoardData([...boardData]);
        }
        let updateValue = value;

        if (valueIsSelectFieldOptionsValue(value, columnId)) {
          if (value.id) {
            updateValue = +value.id;
          }
          if (typeof value.id === "number" && isNaN(value.id)) {
            updateValue = NaN
          }
          if (value.id === undefined || value.label === "") {
            updateValue = NaN;
          }
        }
        if (columnId === "cpu_id") {
          updateValue = value && typeof value === "object" && "label" in value && value.label && value.label !== "" ? value.label : undefined;
          if (value && typeof value === "object" && "id" in value && typeof value.id === "number" && isNaN(value.id)) {
            updateValue = NaN
          }
          if (value && typeof value === "object" && "id" in value && "label" in value && value.id === undefined && value.label === "") {
            updateValue = NaN
          }
        }
        dirtRef.current = Object.values(merge({}, keyBy(dirtRef.current, "controller_id"), keyBy([{
          controller_id: rowIndex,
          [columnId]: updateValue || (typeof updateValue === "number" && isNaN(updateValue)) ? updateValue : null,
        }], "controller_id")));
      },
    },
  });

  const reactiveTable = ReactiveTable({ table, tableType: TableStyles_Types.primaryTable, emptyText: t("devices.noControllersFound", { defaultValue: "no lan controllers found" }), renderFooter: false, tableToggled: editMode, minRows: 7 });

  return (<>
    <div style={{ height: "100%", width: "100%", display: "grid", gridTemplateRows: "50px 1fr", boxSizing: "border-box", overflow: "hidden", zIndex: editMode ? 9999 : 0, position: "relative" }}>
      <div style={{ gridArea: "1/1/2/2", backgroundColor: "transparent", display: "flex", justifyContent: "space-between", alignItems: "end", padding: "0px 20px" }}>
        <p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary, marginBottom: "5px" }}> {t("devices.lanControllers", { defaultValue: "lan controllers" })} </p>
        {!editMode ? (
          <NewButtonList
            contents={[
              t("buttons.add", { defaultValue: "add" }),
              t("buttons.edit", { defaultValue: "edit" }),
            ]}
            onClick={(i) => {
              if (i === 0) { setAddBoardModal(true) }
              if (i === 1) { dispatchEditMode(SET_EDIT_MODE({ enabled: true, type: "lcTable" })); }
            }}
            buttonType={ButtonTypes.tableButton}
            startEnhancers={[glyphs.Add({}), glyphs.Edit({})]}
          />
        ) : (
          <NewButtonList
            contents={[
              t("buttons.save", { defaultValue: "save" }),
              t("buttons.cancel", { defaultValue: "cancel" }),
              t("buttons.add", { defaultValue: "add" }),
              t("buttons.edit", { defaultValue: "edit" }),
            ]}
            onClick={(i) => {
              if (i === 0) {
                if (maintainControllerInfo) { maintainControllerInfo({ data: dirtRef.current }) }
                dispatchEditMode(SET_EDIT_MODE({ enabled: false, type: undefined }));
              }
              if (i === 1) {
                dispatchEditMode(SET_EDIT_MODE({ enabled: false, type: undefined }));
                dirtRef.current = [];
                setBoardData([...lanControllerInfo].map((board, index) => {
                  return {
                    id: board.controller_id,
                    cpu_id: { id: board.cpu_id, label: board.cpu_id ?? "" },
                    name: board.name,
                    system_id: systemOptions.find((system) => system.id === board.system_id),
                    index,
                  };
                }));
              }
            }}
            buttonType={ButtonTypes.tableButton}
            startEnhancers={[glyphs.Check({}), glyphs.Close({}), glyphs.Add({}), glyphs.Edit({})]}
            disabled={[false, false, true, true]}
            overrides={(index) => ({
              baseStyles: { backgroundColor: index === 3 ? colors.greyPrimary : colors.greySecondary },
              disabledStyles: { opacity: index === 3 ? 1 : 0.5 },
            })}
          />
        )}
      </div>
      <div style={{ gridArea: "2/1/3/2", height: "100%", boxSizing: "border-box", position: "relative", backgroundColor: colors.greyLightSecondary, border: "5px solid " + colors.greyPrimary, borderRadius: "10px", overflow: "hidden" }}>
        {reactiveTable}
      </div>
      <div style={{ gridArea: "3/1/4/2" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "5px", padding: "5px 20px 0px 20px", zIndex: 0 }}>
          <NewButton
            content={""}
            buttonType={ButtonTypes.arrowButton}
            onClick={() => {
              if (table.getCanPreviousPage()) { table.previousPage() }
            }}
            startEnhancer={glyphs.Arrow({ rotate: 180 })}
            overrides={{
              baseStyles: { backgroundColor: colors.greyPrimary },
              hoverStyles: { backgroundColor: colors.greySecondary },
            }}
            disabled={!table.getCanPreviousPage()}
          />
          <NewButton
            content={""}
            buttonType={ButtonTypes.arrowButton}
            onClick={() => {
              if (table.getCanNextPage()) { table.nextPage() }
            }}
            startEnhancer={glyphs.Arrow({})}
            overrides={{
              baseStyles: { backgroundColor: colors.greyPrimary },
              hoverStyles: { backgroundColor: colors.greySecondary },
            }}
            disabled={!table.getCanNextPage()}
          />
          {<p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyLightTertiary }}>
            {`${t("page", { defaultValue: "page" })} ${table.getState().pagination.pageIndex + 1} ${t("of", { defaultValue: "of" })} ${table.getPageCount()}`}
          </p>
          }
        </div>
      </div>
    </div>

    {/* Add IO Modal */}
    {addBoardModal && (
      <AddLcModal
        closeModal={() => setAddBoardModal(false)}
        maintainControllerInfo={maintainControllerInfo}
      />
    )}
  </>);
}
